/**
 * Génération PDF du rapport mensuel carburant Royal LT
 * (brief SG-RLT-CDC-MODULES-145 §5.6).
 */
import type { RapportMensuelCarburant } from '@/types';

interface JsPDFInternal {
  internal: { getNumberOfPages: () => number; pageSize: { height: number; width: number } };
  setFontSize: (size: number) => void;
  setFont: (family: string, style?: string) => void;
  setTextColor: (...args: number[]) => void;
  setFillColor: (...args: number[]) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  text: (text: string | string[], x: number, y: number, options?: object) => void;
  addPage: () => void;
  save: (filename: string) => void;
  lastAutoTable?: { finalY: number };
}

const COLOR_PRIMARY: [number, number, number] = [192, 0, 0];
const COLOR_TX_DARK: [number, number, number] = [40, 40, 40];
const COLOR_TX_GREY: [number, number, number] = [110, 110, 110];

const fmtInt = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');

export async function generateRapportMensuelCarburantPDF(data: RapportMensuelCarburant): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as unknown as JsPDFInternal;
  const pageWidth = doc.internal.pageSize.width;

  // En-tête
  doc.setFillColor(...COLOR_PRIMARY);
  doc.rect(0, 0, pageWidth, 8, 'F');

  doc.setTextColor(...COLOR_TX_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('RAPPORT MENSUEL CARBURANT', 14, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_PRIMARY);
  doc.text(data.filiale.fullName, 14, 27);

  doc.setFontSize(10);
  doc.setTextColor(...COLOR_TX_GREY);
  doc.text(`Période : ${data.periode.mois}`, 14, 34);

  doc.setFontSize(8);
  doc.text(`Généré le ${new Date(data.generatedAt).toLocaleString('fr-FR')}`, 14, 40);

  // Section 1 — Synthèse
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('1 · Synthèse opérationnelle', 14, 50);

  autoTable(doc as unknown as object, {
    startY: 53,
    body: [
      ['Conso moyenne flotte (mois)',  data.consoMoyL100kmFlotte !== null ? `${data.consoMoyL100kmFlotte.toFixed(1)} L/100km` : '—'],
      ['Conso moyenne mois précédent', data.consoMoyMoisPrecedent !== null ? `${data.consoMoyMoisPrecedent.toFixed(1)} L/100km` : '—'],
      ['Évolution',                     data.evolutionPct !== null ? `${data.evolutionPct >= 0 ? '+' : ''}${data.evolutionPct.toFixed(1)}%` : '—'],
      ['Économies potentielles (litres)', `${fmtInt(data.economiesPotentielles.litres, 1)} L`],
      ['Économies potentielles (FCFA)',  `${fmtInt(data.economiesPotentielles.fcfa)} FCFA (à ${data.economiesPotentielles.prixLitre} FCFA/L)`],
    ],
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 75, fontStyle: 'bold', textColor: COLOR_TX_GREY },
      1: { halign: 'right', fontStyle: 'bold' },
    },
    theme: 'plain',
  } as Parameters<typeof autoTable>[1]);

  let cursorY = (doc.lastAutoTable?.finalY ?? 90) + 8;

  // Section 2 — Totaux par dépôt
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('2 · Totaux par dépôt', 14, cursorY);
  autoTable(doc as unknown as object, {
    startY: cursorY + 2,
    head: [['Mine', 'Nom', 'Litres distribués', 'Nb bons']],
    body: data.totauxParDepot.map((d) => [
      d.code, d.nom, `${fmtInt(d.litres)} L`, String(d.bons),
    ]),
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
  } as Parameters<typeof autoTable>[1]);
  cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;

  if (cursorY > 230) { doc.addPage(); cursorY = 20; }

  // Section 3 — Top sur-consommateurs
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('3 · Top 10 sur-consommateurs', 14, cursorY);
  if (data.topSurConsommateurs.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['✅ Aucun camion sur-consommateur ce mois.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, fontStyle: 'italic', textColor: [22, 163, 74] },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Rang', 'Camion', 'Modèle', 'Nb bons', 'Litres', 'Ratio moy.', 'Excès (L)']],
      body: data.topSurConsommateurs.map((c, i) => [
        `#${i + 1}`, c.camionCode, c.modele ?? '—', String(c.nbBons), fmtInt(c.litres), `${c.ratioMoyen.toFixed(0)}%`, `+${fmtInt(c.sumEcartL)}`,
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: 12, fontStyle: 'bold' },
        3: { halign: 'right' }, 4: { halign: 'right' },
        5: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] },
        6: { halign: 'right', textColor: [220, 38, 38] },
      },
    } as Parameters<typeof autoTable>[1]);
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  if (cursorY > 230) { doc.addPage(); cursorY = 20; }

  // Section 4 — Top anomalies
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('4 · Top 10 anomalies du mois', 14, cursorY);
  if (data.topAnomalies.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['✅ Aucune anomalie ce mois.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, fontStyle: 'italic', textColor: [22, 163, 74] },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Date', 'N° bon', 'Camion', 'Chauffeur', 'Mine', 'Ratio', 'Gravité', 'Récid.', 'Décision']],
      body: data.topAnomalies.map((a) => [
        new Date(a.date).toLocaleDateString('fr-FR'),
        a.bonNumero, a.camionCode, a.chauffeur, a.mineCode,
        `${a.ratioPct.toFixed(0)}%`, a.gravite, a.estRecidive ? 'OUI' : '—',
        a.decisionFinale ?? '—',
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
    } as Parameters<typeof autoTable>[1]);
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  if (data.recidivistes.length > 0) {
    if (cursorY > 240) { doc.addPage(); cursorY = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(220, 38, 38);
    doc.text('5 · Récidivistes (action RH requise)', 14, cursorY);
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Camion', 'Nb récidives']],
      body: data.recidivistes.map((r) => [r.camionCode, String(r.nbRecidives)]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
    } as Parameters<typeof autoTable>[1]);
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  for (let i = 1; i <= pageCount; i++) {
    (doc as unknown as { setPage: (n: number) => void }).setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_TX_GREY);
    doc.text(`${data.filiale.fullName} — Rapport mensuel carburant — Document confidentiel`, 14, pageHeight - 8);
    doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }

  const filename = `Rapport_Carburant_${data.filiale.name.replace(/\s+/g, '_')}_${data.periode.from.slice(0, 7)}.pdf`;
  doc.save(filename);
}
